import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 8,
  fontSize: 13,
  color: S.textPrimary,
  outline: 'none',
  boxSizing: 'border-box',
};

const displayLabel = (c) => {
  if (!c) return '';
  const name = c.displayName
    || c.companyName
    || [c.firstName, c.lastName].filter(Boolean).join(' ')
    || '(unnamed)';
  return c.clientNumber ? `${name} — ${c.clientNumber}` : name;
};

// Searchable client dropdown backed by GET /clients?search=...
// Emits the full client object via onSelect so the parent can prefill
// downstream form fields (companyName, address) from it.
const ClientSelector = ({ value, onSelect, onClear, disabled }) => {
  const { t } = useTranslation();
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [open, setOpen]             = useState(false);
  const [selectedLabel, setSelLbl]  = useState('');
  const debounceRef                 = useRef(null);
  const wrapperRef                  = useRef(null);

  // Autocomplete pulls up to 500 in one call (server cap). For tenants with
  // more than 500 clients the user must type to narrow the server-side
  // search — the backend applies $or across displayName/clientNumber/email/
  // externalId/firstName/lastName/companyName. Prior limit=25 hid any
  // client beyond the first page (Test Run 7-23 A4.5).
  const fetchClients = useCallback(async (search) => {
    setLoading(true);
    try {
      const url = search
        ? `${BASE_URL}/clients?limit=500&search=${encodeURIComponent(search)}`
        : `${BASE_URL}/clients?limit=500`;
      const resp = await axios.get(url, { withCredentials: true });
      setResults(resp.data?.data || []);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search on every query change.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchClients(query), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open, fetchClients]);

  // Close dropdown on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reflect an externally-controlled value change.
  //
  // The label used to be set ONLY by handlePick, so a value supplied by the
  // parent rather than by the user rendered as an empty box. That is what
  // made Revise look like it had lost the client: the seeded clientId was
  // correct and was submitted on save, but the field showed nothing, so the
  // relationship appeared broken and the natural response was to re-pick
  // the client — which is how a duplicate gets created.
  //
  // Resolve the id to its display name once, on mount or whenever the
  // parent changes it to something we have no label for. Skipped while the
  // user is mid-selection, because handlePick already knows the label.
  useEffect(() => {
    if (!value) { setSelLbl(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BASE_URL}/clients/${value}`, { withCredentials: true });
        const client = r.data?.data?.client || r.data?.data || null;
        if (!cancelled && client) setSelLbl(displayLabel(client));
      } catch {
        // A client that cannot be read (deleted, or out of scope) must not
        // blank the form or throw — leave the field empty and let the user
        // pick again.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handlePick = (client) => {
    setSelLbl(displayLabel(client));
    setOpen(false);
    setQuery('');
    onSelect && onSelect(client);
  };

  const handleClear = () => {
    setSelLbl('');
    setQuery('');
    setResults([]);
    onClear && onClear();
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {selectedLabel && !open ? (
        <div style={{
          ...inputStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? S.bgCard : '#fff',
        }}
        onClick={() => !disabled && setOpen(true)}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedLabel}
          </span>
          <button
            type='button'
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            disabled={disabled}
            title={t('offers.clientSelector.clear', { defaultValue: 'Clear' })}
            style={{ background: 'none', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', padding: 2, marginLeft: 6 }}
          >
            <Icon name='x' color={S.textMuted} size={14} />
          </button>
        </div>
      ) : (
        <input
          type='text'
          value={query}
          onFocus={() => { setOpen(true); if (results.length === 0) fetchClients(''); }}
          onChange={e => setQuery(e.target.value)}
          disabled={disabled}
          placeholder={t('offers.clientSelector.placeholder', { defaultValue: 'Search clients by name, number, email…' })}
          style={inputStyle}
        />
      )}

      {open && !disabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: '#fff',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxHeight: 260,
          overflowY: 'auto',
          zIndex: 20,
        }}>
          {loading && (
            <div style={{ padding: 12, fontSize: 12, color: S.textMuted, textAlign: 'center' }}>
              {t('offers.clientSelector.loading', { defaultValue: 'Loading…' })}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: S.textMuted, textAlign: 'center' }}>
              {t('offers.clientSelector.noResults', { defaultValue: 'No clients found' })}
            </div>
          )}
          {!loading && results.map(c => (
            <div
              key={c._id}
              onClick={() => handlePick(c)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: `1px solid ${S.borderLight}`,
                fontSize: 13,
                color: S.textPrimary,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = S.bgHover || '#f5f5f5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
            >
              <div style={{ fontWeight: 600 }}>
                {c.displayName || c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '(unnamed)'}
              </div>
              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>
                {c.clientNumber}{c.email ? ` · ${c.email}` : ''}{c.type === 'company' ? ' · company' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
